import { motion, useReducedMotion } from 'framer-motion'
import { X } from 'lucide-react'
import type { ComponentType } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useBondActivity, useProtocolStats, coverageRatio, formatCoverage } from '@/hooks/useProtocol'
import { useTokens } from '@/hooks/useTokens'
import { partnerPanelEnsure, partnerPanelHandoff } from '@/lib/api'
import { hidePartnerHandoff, isPartnerHandoffRunning, partnerHandoffStep, showPartnerHandoff } from '@/lib/partner-overlay'
import { openPartnerPanel, signInSameSite } from '@/lib/partner-panel'
import { useWalletGate } from '@/lib/wallet-gate'
import { cn } from '@/lib/cn'
import { BrandTile } from '@/ui/BrandMark'
import {
  IconHome,
  IconBond,
  IconLend,
  IconLite,
  IconMarkets,
  IconMultiply,
  IconPortfolio,
  IconSwap,
} from '@/ui/icons'
import { Readout, ReadoutRow, type Tone } from '@/ui/readout'
import { ReferralLinkCard } from '@/features/referral/ReferralLinkCard'

/**
 * Left navigation rail.
 *
 * ── Why a grid and not a list ──
 * The items are laid out three-per-row rather than stacked. A vertical list of twelve
 * entries needs ~480px of height and leaves the rail's full width empty beside every
 * label; the grid fits the same destinations in a third of the height and gives the
 * rail a reason to be 280px wide.
 *
 * ── Sizing ──
 * The first pass drew these at an 18px glyph over an 11px label inside a 73px tile, which
 * left the icon reading as an afterthought pinned above the word rather than as the thing
 * you aim at. The glyphs are drawn at a 24px viewBox, so 18px was rendering them at 75% and
 * losing the interior detail — the valve stem on Deposit, the dials on Markets — that is the
 * whole reason they were drawn by hand instead of imported.
 *
 * Now 22px on a ~79px tile: the icon is the dominant element and the label captions it, which
 * is the right order for a target you hit by shape and confirm by word. The rail widened
 * 264 → 280 to buy that back without tightening the gaps, and the label went 11px → 12px
 * because eleven is below the size at which a tracked display face stays legible in a
 * glance.
 *
 * ── Motion ──
 * The active-item highlight is a single shared element (`layoutId`), so switching
 * routes SLIDES one pill between tiles instead of cross-fading two. That reads as one
 * indicator moving — which is what it is — and it is the one place here where motion
 * carries meaning rather than decoration. Everything else is a short spring on hover
 * and a staggered entrance, both of which collapse to nothing under
 * `prefers-reduced-motion`.
 */

interface NavItem {
  to: string
  label: string
  /**
   * Hands off to the partner panel instead of routing internally.
   *
   * The destination is minted by our server — it carries a token signed with a secret
   * shared with the partner — so it cannot be a static `to`, and the click has to await
   * it. `to` is still set, to the page the user lands on if the hand-off is unavailable.
   */
  partnerPanel?: boolean
  /*
   * Not `LucideIcon`. The set is drawn in-house (see ui/icons.tsx) because the stock icons came
   * from several idioms at once — a piggy bank for Deposit against an instrument panel. The
   * looser type is what lets a hand-drawn icon and a lucide one coexist while any remain.
   */
  icon: ComponentType<{ className?: string }>
  end?: boolean
}

/**
 * Primary destinations, in the order they are most often needed.
 *
 * Ordered so related routes sit adjacent — the four bond-market entries, then the five
 * lending ones, then the token's own pages. The grid is deliberately unlabelled: the
 * section tab bar at the top of each page already names which product you are in, and
 * repeating that as rail headings said the same thing twice while costing the height
 * that keeps every destination above the fold.
 *
 * Trade (the perpetuals terminal) is intentionally NOT listed. The route, the page and
 * all of its code are still in place and reachable at /trade directly — it is only
 * unlinked from navigation for now. Re-enable by uncommenting the entry below; nothing
 * else needs to change.
 */
const PRIMARY: NavItem[] = [
  { to: '/', label: 'Home', icon: IconHome, end: true },
  { to: '/earn/genesis-bond', label: 'Genesis Bond', icon: IconBond },
  { to: '/earn', label: 'Bonding', icon: IconBond, end: true },
  { to: '/coming-soon/staking', label: 'Staking', icon: IconLend },
  { to: '/swap', label: 'Swap', icon: IconSwap },
  { to: '/coming-soon/legion', label: 'Ninja Army', icon: IconMultiply, partnerPanel: true },
  { to: '/coming-soon/governance', label: 'Governance', icon: IconMarkets },
  { to: '/coming-soon/gitbook', label: 'GitBook', icon: IconLite },
  { to: '/portfolio', label: 'Portfolio', icon: IconPortfolio, end: true },
]

/** Entrance order, so the stagger reads as one continuous sweep. */
const ENTRANCE_INDEX = new Map(PRIMARY.map((item, i) => [item.to, i]))

export function Sidebar({
  onNavigate,
  showBrand = true,
  className,
}: {
  onNavigate?: () => void
  showBrand?: boolean
  /** Lets the mobile drawer constrain this scroll region below its fixed header. */
  className?: string
}) {
  return (
    /* Grow beyond short viewports so every menu item remains reachable by scrolling. */
    <div className={cn('flex min-h-full flex-col gap-4 overflow-y-auto p-3.5 pb-7', className)}>
      {showBrand && <BrandCard />}
      {/*
        Above the menu, matching where people expect to find it. Renders nothing until
        the user has a code, so the rail does not reserve space for an empty box.
      */}
      <ReferralLinkCard />
      <NavGrid onNavigate={onNavigate} />
      {/*
       * Protocol vitals — Supply, Circulating, Treasury, Bonded, Coverage, Bonds.
       *
       * Hidden at the client's request. The `Vitals` component below is left intact rather
       * than deleted: it still compiles, still fetches nothing while unmounted, and restoring
       * the panel is uncommenting this one line.
       */}
      {/* <Vitals /> */}
    </div>
  )
}

function BrandCard() {
  return (
    <motion.div
      // Transform only — see the note on cardEnter in ui/motion.ts.
      initial={{ y: -8 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      /*
       * ── Tall card, not a strip ──
       *
       * This was a horizontal row — mark, name, tagline — 68px tall. The reference gives its
       * logo a 140px card with the mark centred on a gradient field, and the height is doing
       * real work: it is the only element in the rail that is not a control, so it reads as
       * the masthead rather than as the first item in the list. A 68px strip beside 49px nav
       * tiles just reads as a slightly larger nav tile.
       */
      className="relative flex h-[140px] items-center justify-center overflow-hidden rounded-[16px] ring-1 ring-line"
      style={{
        // The reference uses a raster for this. A gradient renders the same read — a lit
        // corner falling away to the card's own ground — without a second asset to ship.
        background:
          'radial-gradient(120% 90% at 18% 12%, var(--color-accent-wash), transparent 62%), var(--color-surface)',
      }}
    >
      <div className="flex flex-col items-center gap-2">
        <BrandTile className="size-12 rounded-[12px]" />
        <p className="display text-xl font-bold leading-none">
          Ninja<span className="text-accent">DEX</span>
        </p>
      </div>
    </motion.div>
  )
}

function NavGrid({ onNavigate }: { onNavigate?: () => void }) {
  const reduced = useReducedMotion()
  const { pathname } = useLocation()
  // Keep the full portal map visible before sign-in. The route itself presents the
  // sign-in screen, so users can discover destinations without bypassing access rules.
  const visibleItems = PRIMARY

  /**
   * Which tile owns the highlight.
   *
   * Resolved here, once, rather than letting each NavLink decide: `layoutId` needs
   * exactly one element to exist at a time, and two tiles both believing they are
   * active (`/lend` and `/lend/borrow` under a prefix match) would mount two pills and
   * make the shared-element animation jump. Longest match wins, so a sub-route always
   * beats its parent.
   */
  const activeTo = PRIMARY.reduce<string | null>((best, item) => {
    const matches = item.end ? pathname === item.to : pathname.startsWith(item.to)
    if (!matches) return best
    return best === null || item.to.length > best.length ? item.to : best
  }, null)

  return (
    <nav aria-label="Main">
      <p className="label mb-3 flex items-center gap-2 px-1">
        Menu
        <span className="h-px flex-1 bg-line" aria-hidden />
      </p>
      {/* 14px w600 — the reference's one menu heading. */}
      <p className="mb-3 text-sm font-semibold text-ink">Menu</p>
      <ul className="grid grid-cols-3 gap-y-6">
        {visibleItems.map((item) => (
          <NavTile
            key={item.to}
            item={item}
            isActive={activeTo === item.to}
            reduced={Boolean(reduced)}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </nav>
  )
}

function NavTile({
  item,
  isActive,
  reduced,
  onNavigate,
}: {
  item: NavItem
  isActive: boolean
  reduced: boolean
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  // The hand-off signs THIS wallet in, so both come from the shared gate rather than
  // being threaded down as props from wherever the rail happens to be rendered.
  const { address, openPicker } = useWalletGate()

  return (
    <motion.li
      // Transform only. With opacity here, a tab switch mid-stagger left the later tiles
      // permanently invisible — most of the navigation, gone until reload.
      initial={reduced ? false : { y: 6 }}
      animate={{ y: 0 }}
      transition={{
        duration: 0.3,
        // Staggered here rather than by a parent's `staggerChildren`: variant
        // propagation reaches a child component through context, and the moment the
        // tile moved into its own component the orchestration stopped arriving —
        // leaving every tile but the first stuck at opacity 0. An explicit delay
        // cannot silently fail that way.
        delay: reduced ? 0 : (ENTRANCE_INDEX.get(item.to) ?? 0) * 0.03,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <NavLink
        to={item.to}
        end={item.end}
        onClick={(event) => {
          /*
           * The hand-off replaces the route only when it succeeds. If the panel is
           * unconfigured, the session has lapsed, or the referral is unsettled, the
           * click falls through to `to` and the user sees a page rather than nothing —
           * a dead tab is a worse failure than the placeholder it replaced.
           *
           * `preventDefault` happens BEFORE the await so React Router does not navigate
           * out from under the request. On failure the navigation is performed
           * explicitly instead.
           */
          if (item.partnerPanel) {
            event.preventDefault()

            /*
             * ── First choice on the deployed domain: just sign in ──
             *
             * `defi.ninjadex.org` and `api.ninjadex.org` are the same SITE, so the partner's
             * `SameSite=Lax` session cookie is stored by an ordinary `fetch` — and their API
             * already returns `access-control-allow-origin` for our origin with
             * `allow-credentials: true`. No tab, no form, no loader, and it behaves the same
             * in every browser including MetaMask's webview, which cannot be handed a
             * driveable window.
             *
             * Everything after this is the fallback for origins that are genuinely
             * cross-site — a developer on localhost, or a future deployment on another
             * domain — where a Lax cookie really would be dropped.
             */
            /*
             * Register the wallet upstream FIRST, then sign in.
             *
             * The partner cannot authenticate an address it has never been told about, and
             * binding a referral only queues that hand-off. Asking for it explicitly turns a
             * race against a worker interval into an ordinary request, so the sign-in that
             * follows finds an account instead of a 404.
             *
             * A failure here is not fatal: an established wallet is already registered and
             * does not need this, so we continue either way and let the sign-in decide.
             */
            /*
             * No wallet, no hand-off. The partner authenticates an ADDRESS, so without
             * one the login can only ever answer NOT_FOUND -- twenty seconds of retries
             * ending at their login form. Ask for the wallet instead.
             */
            if (!address) {
              partnerHandoffStep('No wallet connected', 'opening the picker')
              openPicker()
              return
            }

            /*
             * A second click must not start a second registration + sign-in. The overlay's
             * presence IS the in-flight flag, so the guard cannot drift from what the user
             * can see.
             */
            if (isPartnerHandoffRunning()) return
            showPartnerHandoff()
            partnerHandoffStep('Registering your wallet')
            void partnerPanelEnsure()
              .then((r) => { partnerHandoffStep('Wallet registered', `ready=${r.ready}`); return r })
              .catch((e: unknown) => {
                // Not fatal: an established wallet is already registered upstream.
                partnerHandoffStep('Registration skipped', e instanceof Error ? e.message : 'failed')
                return { ready: false }
              })
              .then(() => {
                partnerHandoffStep('Signing you in')
                /*
                 * The second argument is the RE-REGISTRATION hook, not a referral code.
                 * A brand-new wallet's first sign-in legitimately answers NOT_FOUND, and
                 * this asks for the upstream row directly rather than waiting on a worker
                 * interval to happen to fire inside the retry budget.
                 */
                return signInSameSite(address, () => {
                  partnerHandoffStep('Registering upstream', 'partner had no account yet')
                  return partnerPanelEnsure()
                })
              })
              .then((url) => {
              if (url) {
                partnerHandoffStep('Opening your dashboard')
                window.location.assign(url)
                return
              }
              partnerHandoffStep('Sign-in did not complete', 'falling back')

                          /*
             * ── Legacy: MetaMask's in-app browser goes straight to the partner ──
             *
             * Its webview hands back a window that cannot be driven, so the direct flow
             * cannot finish there: the loader waits out its timeout and the user arrives at
             * the partner's login anyway, having watched a spinner for twelve seconds first.
             * Skipping the attempt costs nothing and saves all of it.
             *
             * This is NOT the seamless outcome. The partner's login page ignores the signed
             * token we mint — its whole script is a submit listener, with no `location.hash`
             * handling — so it prefills nothing and requires a tap. Until they consume the
             * token, one tap is the floor for this browser, and a fast tap beats a slow one.
             *
             * Detected by user agent rather than `window.ethereum?.isMetaMask`: the desktop
             * extension sets that too, and desktop completes the direct flow perfectly.
             */
            if (/MetaMaskMobile/i.test(navigator.userAgent)) {
                partnerHandoffStep('MetaMask browser', 'using the signed hand-off')
                void partnerPanelHandoff()
                  .then(({ url: u }) => window.location.assign(u))
                  .catch((e: unknown) => { partnerHandoffStep('Hand-off failed', e instanceof Error ? e.message : 'unknown'); hidePartnerHandoff(); navigate(item.to) })
                return
              }

            /*
             * ── Everything else attempts the direct flow ──
             *
             * MetaMask's mobile webview used to be short-circuited to the signed SSO
             * hand-off, on the reasoning that its popup/opener relationship is unreliable.
             * That reasoning was sound but the destination is not: the partner's login page
             * DOES NOT READ THE TOKEN. Its entire script is a submit listener — no
             * `location.hash`, no verification, no auto-submit — so the hand-off lands the
             * user on a manual form and the signature we mint is ignored. What looked like
             * a prefilled hand-off was the browser's own autocomplete.
             *
             * So the token route is not a seamless fallback; it is a worse outcome for every
             * browser that takes it. Mobile Safari, Chrome and Brave all honour
             * `window.open` from a user gesture, and the direct flow works there exactly as
             * it does on desktop. Attempting it costs nothing when it fails — `openPartnerPanel`
             * already falls back to the token URL on a blocked or undriveable tab.
             *
             * MetaMask's in-app browser may still end up on that form. Only the partner
             * consuming the fragment token can fix that, and this is tracked with them.
             */

            /*
             * The tab is opened SYNCHRONOUSLY, before the request, and driven afterwards.
             *
             * A `window.open` issued after an await is no longer attributed to the click,
             * which is precisely what popup blockers reject. Claiming the tab first keeps
             * it user-initiated.
             *
             * It is also where the sign-in happens rather than merely where it lands: the
             * session is planted by navigating THIS tab to their login, because only a
             * top-level navigation makes their `SameSite=Lax` cookie first-party. See
             * partner-panel.ts.
             *
             * Opened on a real path rather than `''`, which would leave `about:blank` in
             * the address bar for the whole sign-in.
             */
            partnerHandoffStep('Opening a tab for the hand-off')
            const tab = window.open('/partner-connecting.html', '_blank')
            /*
             * ── `tab.opener` is deliberately NOT cleared ──
             * Clearing it is the usual tabnabbing precaution, and it breaks this flow
             * outright. Permission to navigate a cross-origin window comes from being its
             * opener, so severing the link makes the final redirect fail with:
             *
             *   SecurityError: The current window does not have permission to navigate
             *   the target frame to https://client.ninjadex.org/index.html
             *
             * The tab would then sit on the login endpoint's raw JSON response. Since the
             * destination is the partner panel we are deliberately signing the user into,
             * keeping the opener is the accepted trade.
             */

            void openPartnerPanel(tab, address ?? '')
              .then(({ url }: { url: string }) => {
                if (tab) {
                  tab.location.href = url
                  tab.focus()
                  // The panel opened in the OTHER tab; this one stays on the app.
                  partnerHandoffStep('Opened in a new tab')
                  hidePartnerHandoff()
                } else {
                  // Blocked. Better the panel in this tab than nothing at all.
                  window.location.assign(url)
                }
              })
              .catch(() => {
                  /*
                   * Nothing to show there, so the blank tab is closed rather than left
                   * sitting on about:blank, and the click falls through to the route the
                   * tile would have gone to anyway.
                   */
                  tab?.close()
                  partnerHandoffStep('Tab hand-off failed')
                  hidePartnerHandoff()
                  navigate(item.to)
                })
            })
            /*
             * Terminal catch for the WHOLE chain.
             *
             * Without it a rejection anywhere above leaves the overlay mounted -- and it is a
             * fixed, full-viewport, top-of-stack element, so a stuck overlay does not merely
             * lose the loader, it locks the entire app behind an unclickable veil.
             */
            .catch((e: unknown) => {
              partnerHandoffStep('Aborted', e instanceof Error ? e.message : 'unknown')
              hidePartnerHandoff()
              navigate(item.to)
            })
          }
          onNavigate?.()
        }}
        aria-current={isActive ? 'page' : undefined}
        /*
         * ── Active is the LABEL's colour, not a filled pill ──
         *
         * The reference marks its active destination by colouring the icon and label; the
         * tile itself stays as it is. In a row list a filled pill works, because the row is
         * wide enough to hold a shape. At this tile width a filled housing is nearly the whole
         * column, so the highlight stops marking one item and starts looking like a block of
         * the rail is selected. The sliding `layoutId` pill that used to animate between
         * tiles went with it — there is no longer a shape for it to move.
         */
        className={cn(
          'group relative flex h-[49px] flex-col items-center justify-center gap-1 px-1',
          'transition-colors duration-150',
        )}
      >
        {/*
         * Hover lift is on the icon alone: moving the whole tile would shift its label and make
         * the grid feel loose.
         *
         * `whileTap` seats the icon 1px INTO the panel rather than scaling it — a real panel
         * button travels, it does not shrink. Paired with the tile's own active box-shadow below,
         * it gives the press somewhere to go.
         */}
        <motion.span
          className="relative"
          whileHover={reduced ? undefined : { y: -2 }}
          whileTap={reduced ? undefined : { y: 1, scale: 0.94 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <item.icon
            className={cn('size-6', isActive ? 'text-accent' : 'text-muted')}
            aria-hidden
          />
        </motion.span>
        {/* 12/18 w400. Truncates rather than wraps: a two-line label makes one tile taller
            than its row and the grid's baseline comes apart. */}
        <span
          className={cn(
            'relative max-w-full truncate text-xs leading-[18px]',
            isActive ? 'text-accent' : 'text-muted',
          )}
        >
          {item.label}
        </span>
      </NavLink>
    </motion.li>
  )
}

/**
 * Live protocol vitals.
 *
 * These used to sit in a full-width ticker under the header. With the header gone
 * they move here, which suits them better — they are ambient reference figures, not
 * something you act on, so a quiet column beside the content is the right weight.
 */
// Exported so it is not flagged as unused while the rail hides it — see the commented-out
// <Vitals /> above. Deleting it would mean rewriting the component to bring the panel back.
export function Vitals() {
  const { stats } = useProtocolStats()
  const { totals } = useBondActivity()
  const tokens = useTokens()

  const coverage = stats ? coverageRatio(stats.treasuryNdx, totals.outstanding) : null

  /*
   * Tone is SEMANTIC here, not a good/bad ranking.
   *
   * Coverage is a reward-side figure, so it takes brass when it is sound and fault only when
   * the treasury is genuinely short — which is a different statement from "green is nice".
   * Treasury and Bonded are stablecoin amounts, so they take fluid. Supply, circulating and
   * the bond count are plain protocol counts and stay ink. Every one of those pairings is the
   * deck's rule rather than a preference.
   */
  const rows: { label: string; value: string; tone: Tone }[] = stats
    ? [
        { label: 'Supply', value: tokens.ndxCompact(stats.ndxSupply), tone: 'ink' },
        { label: 'Circulating', value: tokens.ndxCompact(stats.circulating), tone: 'ink' },
        { label: 'Treasury', value: tokens.payCompact(stats.treasuryPayment), tone: 'fluid' },
        { label: 'Bonded', value: tokens.payCompact(totals.paid), tone: 'fluid' },
        {
          label: 'Coverage',
          value: formatCoverage(coverage),
          tone: coverage === null ? 'muted' : coverage >= 1 ? 'brass' : 'fault',
        },
        { label: 'Bonds', value: String(stats.bondsSold), tone: 'ink' },
      ]
    : []

  return (
    <motion.section
      initial={{ y: 8 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.35, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <p className="label mb-3 flex items-center gap-2 px-1">
        Protocol
        <span className="h-px flex-1 bg-line" aria-hidden />
      </p>
      {rows.length === 0 ? (
        <Readout>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="px-3 py-1.5">
              <div className="h-3 animate-pulse rounded bg-skeleton" />
            </div>
          ))}
        </Readout>
      ) : (
        <Readout>
          {rows.map((r) => (
            <ReadoutRow key={r.label} label={r.label} value={r.value} tone={r.tone} dense />
          ))}
        </Readout>
      )}
    </motion.section>
  )
}

// ─────────────────────────── Mobile drawer ───────────────────────────

export function SidebarDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const reduced = useReducedMotion()

  // Do not keep an exiting full-screen scrim mounted after navigation. It can be visually
  // transparent while still intercepting the next tap on the hamburger, especially when a
  // user moves from Portfolio straight back to Home.
  const drawer = open ? (
    <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            /*
             * `scrim/40`, not `black/60`. Pure black at 60% is a dark-theme value; over a
             * silver hall it reads as a hole punched through the page rather than as the page
             * dimmed behind a drawer.
             */
            className="fixed inset-0 z-40 bg-scrim/40 backdrop-blur-sm lg:hidden"
            aria-hidden
          />
          <motion.aside
            initial={{ x: reduced ? 0 : '-100%' }}
            animate={{ x: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 40 }}
            className="surface-glass lift fixed inset-y-0 left-0 z-50 flex h-dvh w-[min(88vw,320px)] flex-col overflow-hidden border-r border-line pb-[env(safe-area-inset-bottom)] lg:hidden"
            role="dialog"
            aria-label="Navigation"
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4 pt-[max(1rem,env(safe-area-inset-top))]">
              <div className="flex min-w-0 items-center gap-2.5">
                <BrandTile className="size-9 shrink-0 rounded-[10px] [box-shadow:var(--btn-primary-shadow)]" />
                <p className="display truncate text-lg">
                  Ninja<span className="text-accent">DEX</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close navigation"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-elevated px-2.5 py-2 text-xs font-medium text-muted transition-colors hover:border-accent-edge hover:bg-accent-wash hover:text-ink active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <X className="size-3.5 stroke-[2.25]" aria-hidden />
                Close
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <Sidebar className="h-full min-h-0" showBrand={false} onNavigate={onClose} />
            </div>
          </motion.aside>
    </>
  ) : null

  // Mount outside route content so a page-specific transform, animation or stacking context
  // can never sit above the mobile navigation controls. This is especially important on dense
  // portfolio screens that contain animated cards and transaction feedback layers.
  return typeof document === 'undefined' ? drawer : createPortal(drawer, document.body)
}
